"use client";

import React, { useState, useEffect, Fragment } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
const EXCLUDED_LABELS = ["wembley", "london neighborhood pages", "navigation"];
function CustomMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="w-64 bg-background border border-border p-6 flex flex-col gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-full bg-muted animate-pulse" />)}
      </div>;
  }
  return <div className="w-64 bg-background border border-border shadow-2xl p-6 flex flex-col gap-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onClose} data-index={i} className="text-xs font-sans font-bold uppercase tracking-[0.2em] hover:text-foreground/60 transition-colors">
          {item.label}
        </Link>)}
    </div>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3].map(i => <div key={i} className="h-4 w-16 bg-muted animate-pulse" />)}
      </div>;
  }
  const filteredItems = menuItems.filter(item => !EXCLUDED_LABELS.includes(item.label.toLowerCase()));
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {filteredItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-xs font-sans font-bold uppercase tracking-[0.2em] hover:text-foreground/60 transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function DesktopMenu() {
  return <MenuProvider menu_id="25">
      <DesktopMenuConsumer />
    </MenuProvider>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-8 mt-12">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-2/3 bg-muted animate-pulse" />)}
      </div>;
  }
  const filteredItems = menuItems.filter(item => !EXCLUDED_LABELS.includes(item.label.toLowerCase()));
  return <div className="mt-12 flex flex-col gap-8">
      {filteredItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onClose} data-index={i} className="text-4xl font-sans font-extrabold uppercase tracking-tighter hover:text-muted-foreground transition-colors">
          {item.label}
        </Link>)}
    </div>;
}
function MobileMenuContent({
  onClose
}: {
  onClose: () => void;
}) {
  return <nav className="flex flex-col gap-8 p-8 mt-16">
      <MenuProvider menu_id="25">
        <MobileMenuConsumer onClose={onClose} />
      </MenuProvider>
    </nav>;
}
export default function SolidTrustNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCustomMenuOpen, setIsCustomMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
            setIsCustomMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);
  return <Fragment>
      <header id="solidtrust-navigation" className={`dark sticky top-0 z-40 w-full transition-all duration-500 text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
        <div className="container mx-auto px-6 h-24 flex items-center justify-between">
          <Link to="/" className="flex-shrink-0 z-50 relative">
            <img src="https://solidtrustbuilder.com/wp-content/uploads/2026/03/1a092238-d084-427b-879c-6eb67dbd4e34.png" alt="SolidTrust Builders" className="object-contain !h-[100px] !w-[90px]" />
          </Link>

          <div className="hidden md:flex items-center justify-center flex-1 px-12">
            <DesktopMenu />
          </div>

          <div className="flex items-center gap-4">
            <div className="relative">
              <Button variant="ghost" size="icon" className="z-50 bg-transparent hover:bg-transparent rounded-none p-0 h-16 w-16 flex items-center justify-center focus-visible:ring-0" onClick={() => setIsCustomMenuOpen(!isCustomMenuOpen)} aria-label="Toggle custom menu">
                <img src="https://solidtrustbuilder.com/wp-content/uploads/2026/03/ChatGPT-Image-Mar-14-2026-04_59_33-PM.png" alt="Menu Icon" className="h-12 w-auto object-contain brightness-0 invert" />
              </Button>
              {isCustomMenuOpen && <div className="absolute top-full right-0 mt-6 z-50">
                  <MenuProvider menu_id="27">
                    <CustomMenuConsumer onClose={() => setIsCustomMenuOpen(false)} />
                  </MenuProvider>
                </div>}
            </div>

            
          </div>
        </div>
      </header>

      {isMobileMenuOpen && <div className="dark fixed inset-0 z-30 bg-background text-foreground md:hidden pt-24 overflow-y-auto">
          <MobileMenuContent onClose={() => setIsMobileMenuOpen(false)} />
        </div>}
    </Fragment>;
}